/**
 * #694: contracts and comparison logic for the recurring memory evaluation that tells operators whether
 * enterprise memory (#686-#692) is improving task outcomes or quietly degrading. Held here, once, the way
 * memory-graph.ts holds the Operations → Memory read path's pure predicates once — so apps/api's
 * `MemoryEvalService` and apps/portal's trend view judge a run identically rather than each guessing at their
 * own scoring or labelling.
 *
 * Everything here is deliberately pure and deliberately dull: reference-fact coverage of one answer against
 * another, an average, a comparison of two snapshots. A score this file produces is only ever a claim about
 * whether the stated reference facts were recalled, never a claim that an answer was good — which is why the
 * questions are recall probes built from versioned engineering templates (`memory-eval-questions.ts`, #694 CTO
 * decision 4 as revised) and a regression always points back at the probes that dropped rather than at a
 * number on its own.
 */

/**
 * One question the recurring evaluation asks. Since #694's re-triage (CTO decision 5) every question is a
 * recall probe: a versioned template instantiated against one pinned entry of the evaluated enterprise's own
 * memory, so its reference facts are something that enterprise actually knows.
 */
export type MemoryEvalQuestion = {
    id: string
    prompt: string
    /**
     * The facts a correct answer should recall, judged as case-insensitive substrings of the answer once the
     * answer's echo of `prompt` is taken out (see `recalledContent`) and whitespace is collapsed.
     */
    referenceFacts: string[]
}

/** One arm's answer to a question: the text produced, and the evidence it cites to support it. */
export type MemoryEvalAnswer = {
    answer: string
    evidence: string[]
}

/**
 * The paired result for one question: both arms' answers, run under the identical question and model
 * configuration (#694 AC1 — the only way a later score delta can be attributed to memory rather than to
 * something that changed about how the question was asked), and the score memory made to it.
 */
export type MemoryEvalQuestionResult = {
    questionId: string
    memoryAssisted: MemoryEvalAnswer
    baseline: MemoryEvalAnswer
    /** How much of the reference facts the memory-assisted arm recalled, from 0 to 1. */
    memoryAssistedScore: number
    /**
     * How much of the reference facts the memory-disabled baseline recalled, from 0 to 1 — measured and recorded
     * on every result, not assumed (#694 CTO decision 8). For a recall probe it is 0 by construction: with
     * memory off there is nothing to recall from, and keeping that 0 on the record is what makes the
     * comparison auditable.
     */
    baselineScore: number
    /** memory-assisted reference-fact coverage minus the baseline's: positive is memory helping, negative is memory hurting. */
    score: number
}

/** The model configuration, cost and data-volume snapshot a run executed under (#694 AC2). */
export type MemoryEvalRunConfig = {
    model: string
    costCents: number
    dataVolumeBytes: number
    /**
     * The version of the probe templates this run's questions were built from
     * (`MEMORY_EVAL_PROBE_TEMPLATE_VERSION`). Optional only so a caller that scores a question set of its own —
     * a test, or a one-off investigation — is not forced to invent a version for it; every scheduled run
     * records one.
     *
     * It is part of the configuration for the same reason the model is: changing the templates changes what
     * the score measures, so a score that moves across a template bump is `explained` by that bump rather than
     * an unexplained regression an operator should chase.
     */
    questionSetVersion?: string
    /**
     * The revision of this enterprise's pinned probe set the run asked (#694 CTO decision 7). The probes are
     * pinned and reused run after run, so with memory unchanged the score is unchanged; the revision moves only
     * when the set itself does — first pinned, topped up as memory grows, or re-pinned after a template bump.
     * It is configuration for the same reason `questionSetVersion` is: a score that moves because the probes
     * changed is `explained`, never reported as a quality regression.
     */
    probeSetRevision?: number
}

/** Whether a score change between two runs is accounted for by a recorded config/cost/volume change (#694 AC2). */
export type MemoryEvalRegressionLabel = 'unexplained_regression' | 'explained' | 'improvement' | 'no_change'

/** The minimal shape `labelMemoryEvalDelta` needs from a run to judge one against another. */
export type MemoryEvalRunSnapshot = {
    aggregateScore: number
    config: MemoryEvalRunConfig
}

/**
 * One recorded run: its config snapshot, its per-question results and aggregate score, and how it compares
 * with the enterprise's previous run.
 */
export type MemoryEvalRun = {
    id: string
    enterpriseId: string
    createdAt: string
    config: MemoryEvalRunConfig
    questionResults: MemoryEvalQuestionResult[]
    aggregateScore: number
    /** null only when there was no previous run for this enterprise to compare against. */
    regressionLabel: MemoryEvalRegressionLabel | null
    /** How many probes the run asked: `questionResults.length`, kept beside it so the trend can read it. */
    probeCount: number
}

/** One run as the recurring trend lists it: enough to plot and to label, without every question's answer text. */
export type MemoryEvalRunSummary = {
    id: string
    createdAt: string
    config: MemoryEvalRunConfig
    aggregateScore: number
    regressionLabel: MemoryEvalRegressionLabel | null
    /**
     * How many probes the run asked (#694 CTO decision 9). `0` means the enterprise's memory could not supply
     * a probe — not enough memory to evaluate — which is a different fact from a run that asked its probes and
     * scored 0, so the trend shows it differently rather than plotting a zero an operator would read as
     * "memory is not helping". Optional only so a summary built by hand without one still reads as a scored
     * run; every run `MemoryEvalService` records carries it.
     */
    probeCount?: number
}

/** Whether a run asked no probes at all: its enterprise's memory had nothing to evaluate it on (#694 CTO decision 9). */
export function hadNoProbes(run: Pick<MemoryEvalRunSummary, 'probeCount'>): boolean {
    return run.probeCount === 0
}

/**
 * A run's detail: its per-question results — narrowed to the regressed ones when the caller asked for that —
 * and which questions regressed regardless of that narrowing (#694 AC3).
 */
export type MemoryEvalRunDetail = MemoryEvalRun & {
    regressedQuestionIds: string[]
}

/**
 * A score difference smaller than this is arithmetic noise, not a change in behaviour: an aggregate is an
 * average of floats, so two runs that answered every question identically can still differ in the last bits.
 * Below it, two runs are reported as unchanged rather than as a regression an operator should chase.
 */
export const MEMORY_EVAL_SCORE_EPSILON = 1e-6

/** Lower-cased with every run of whitespace collapsed to one space, so a line break never hides a fact. */
function normalise(text: string): string {
    return text.toLowerCase().replace(/\s+/g, ' ').trim()
}

/**
 * The part of an answer that was recalled rather than repeated: the answer with every echo of the question's
 * own prompt taken out (#694 re-triage, slice 4). Ask's answer opens by restating the question, so without
 * this a fact that also appears in the prompt would score as recalled whenever the prompt was asked, whatever
 * memory returned.
 */
function recalledContent(question: MemoryEvalQuestion, answer: MemoryEvalAnswer): string {
    const said = normalise(answer.answer)
    const prompt = normalise(question.prompt)
    return prompt === '' ? said : said.split(prompt).join(' ')
}

/** How much of a question's reference facts one answer recalled, as a fraction of them: 0 when it names none. */
function referenceFactCoverage(question: MemoryEvalQuestion, answer: MemoryEvalAnswer): number {
    if (question.referenceFacts.length === 0) return 0
    const recalled = recalledContent(question, answer)
    const stated = question.referenceFacts.filter(fact => recalled.includes(normalise(fact))).length
    return stated / question.referenceFacts.length
}

/**
 * The paired comparison for one question (#694 AC1): how much of the question's known-good facts the
 * memory-assisted arm stated, minus how much the memory-disabled baseline stated of the same facts.
 *
 * The subtraction is the point. Both arms answered the same question under the same model configuration, so a
 * positive score is memory adding a fact the baseline could not reach, and a negative one is memory losing a
 * fact the baseline still had — neither is a claim about the model's general ability, which cancels out.
 * A question with no reference facts scores 0: there is nothing recorded to judge either answer against, so
 * this reports no difference rather than inventing one.
 */
export function compareMemoryEvalAnswers(
    question: MemoryEvalQuestion,
    memoryAssisted: MemoryEvalAnswer,
    baseline: MemoryEvalAnswer,
): MemoryEvalQuestionResult {
    const memoryAssistedScore = referenceFactCoverage(question, memoryAssisted)
    const baselineScore = referenceFactCoverage(question, baseline)
    return {
        questionId: question.id,
        memoryAssisted,
        baseline,
        memoryAssistedScore,
        baselineScore,
        score: memoryAssistedScore - baselineScore,
    }
}

/**
 * The aggregate score across a run's questions (#694 AC1): the mean of the per-question scores, so every
 * representative question weighs the same and the aggregate stays comparable run over run as long as the
 * question-set version does. A run with no results scores 0 rather than `NaN`.
 */
export function aggregateMemoryEvalScore(results: MemoryEvalQuestionResult[]): number {
    if (results.length === 0) return 0
    return results.reduce((total, result) => total + result.score, 0) / results.length
}

/**
 * Whether anything about how a run was executed — its model, its cost, the data behind it, the version of the
 * probe templates, or the revision of the enterprise's pinned probe set — changed (#694 AC2, CTO decision 7).
 */
function configChanged(previous: MemoryEvalRunConfig, current: MemoryEvalRunConfig): boolean {
    return (
        previous.model !== current.model ||
        previous.costCents !== current.costCents ||
        previous.dataVolumeBytes !== current.dataVolumeBytes ||
        previous.questionSetVersion !== current.questionSetVersion ||
        previous.probeSetRevision !== current.probeSetRevision
    )
}

/**
 * How one run's score compares with the previous run's (#694 AC2), which is what keeps a quality change apart
 * from a change in cost, model configuration or data volume:
 *
 * - `no_change` — the two scores differ by less than `MEMORY_EVAL_SCORE_EPSILON`, so nothing moved.
 * - `explained` — the score moved and the recorded configuration moved with it. The evaluation will not claim
 *   the difference for memory either way: a drop after a model downgrade, and a rise after an upgrade, are
 *   both attributable to the configuration this run executed under rather than to what memory knows.
 * - `improvement` — the score rose under an unchanged configuration: memory answered more of the known-good
 *   facts than it did before.
 * - `unexplained_regression` — the score fell under an unchanged configuration. Nothing recorded accounts for
 *   it, so it is the case an operator is meant to notice and trace back to its questions.
 */
export function labelMemoryEvalDelta(
    previous: MemoryEvalRunSnapshot,
    current: MemoryEvalRunSnapshot,
): MemoryEvalRegressionLabel {
    const delta = current.aggregateScore - previous.aggregateScore
    if (Math.abs(delta) < MEMORY_EVAL_SCORE_EPSILON) return 'no_change'
    if (configChanged(previous.config, current.config)) return 'explained'
    return delta > 0 ? 'improvement' : 'unexplained_regression'
}

/**
 * The questions whose score dropped between two runs, each carrying the current run's answer and evidence for
 * it (#694 AC3) — so "the trend dropped" leads straight to the representative questions that caused it and to
 * what memory actually answered them with, rather than stopping at an aggregate.
 *
 * A question the previous run did not ask is not a drop: there is no earlier score to have fallen from, so a
 * question set gaining a question does not read as a regression.
 */
export function regressedQuestions(
    previous: MemoryEvalQuestionResult[],
    current: MemoryEvalQuestionResult[],
): MemoryEvalQuestionResult[] {
    const before = new Map(previous.map(result => [result.questionId, result.score]))
    return current.filter(result => {
        const earlier = before.get(result.questionId)
        return earlier !== undefined && earlier - result.score >= MEMORY_EVAL_SCORE_EPSILON
    })
}
